import { useState } from 'react';
import { Box, Stack } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { BrandLogo } from '~portal/components/BrandLogo';
import { Cmp } from '~portal/lib';
import { AccountBlock } from './AccountBlock';
import * as styles from './GlobalNav.css';
import { GlobalNavItems } from './GlobalNavItems';
import { LazyAdminNavPanel } from './LazyAdminNavPanel';
import { useNavMode } from './nav-mode';
import { NavSettings } from './NavSettings';

/**
 * @desc Whether the column shows the admin tree, and the slide its contents enter with. The slide
 * plays only on a swap — "Admin →", "← Back" or a navigation that changes the list — never on the
 * first paint of the page.
 */
export function useFoldOver(): { inAdmin: boolean; foldClass: string | undefined } {
  const inAdmin = useNavMode().mode === 'admin';
  const [first] = useState(inAdmin);
  const [swapped, setSwapped] = useState(false);
  // Latched: once the column has swapped, every later swap slides too, including the one back.
  if (inAdmin !== first && !swapped) setSwapped(true);
  if (!swapped && inAdmin === first) return { inAdmin, foldClass: undefined };
  return { inAdmin, foldClass: inAdmin ? styles.foldInAdmin : styles.foldInSections };
}

/**
 * @desc The platform's desktop global navigation: a full-height column left of every non-chat
 * page — logo (home), the five section pills, then the settings row and the account block
 * pinned to the bottom. Below `md` it is replaced by `GlobalNavMobileHeader` + `GlobalNavDrawer`,
 * which render the same items.
 *
 * The column folds over to the admin tree (D3) — by default on an `/admin/*` page, and whenever an
 * administrator presses "Admin →" (D4), which swaps the list without navigating: the pills give
 * way to a Back item and the admin tree, in the same column — same width, position and surface, settings row and account
 * block still pinned — so an admin page has one navigation column, not two.
 */
export const GlobalNav: Cmp = function () {
  const { inAdmin, foldClass } = useFoldOver();
  return (
    <Box
      component="nav"
      aria-label="Main"
      visibleFrom="md"
      className={styles.column}
      data-fold={inAdmin ? 'admin' : 'sections'}
    >
      <Link to="/" aria-label="H2GC home" className={styles.logoLink}>
        <BrandLogo height={70} />
      </Link>
      {inAdmin ? (
        // The admin tree is long (five groups), so it starts closer under the logo than the pills.
        <Box
          key="admin"
          mt="xl"
          className={[styles.adminRegion, foldClass].filter(Boolean).join(' ')}
        >
          <LazyAdminNavPanel />
        </Box>
      ) : (
        // The frames start the first pill at y≈190, ~72px below the logo's foot.
        <Box key="sections" mt={72} className={foldClass}>
          <GlobalNavItems />
        </Box>
      )}
      <Stack gap="sm" mt="auto" pt="lg">
        <NavSettings />
        <AccountBlock />
      </Stack>
    </Box>
  );
};
